import _ from 'lodash'
import { cloneDeep, set, uniqBy } from 'lodash'
import { Observable, from, of } from 'rxjs'
import { concatMap, first, map, switchMap } from 'rxjs/operators'

import { Collaborator, Item, User, Movement, MovementLifecycleStates, MovementTypes, LinkableItemLifecycleStates, LinkableItem, Task, Location } from '@skinsoft/types'
import { itemSelectors, movementSelectors, taskSelectors } from '@skinsoft/selectors'
import { AuthService } from '@skinsoft/auth'
import { EntityDataStore, } from '@skinsoft/entity-data-store'
import { ItemDataStore } from '../item/item-data-store'
import { TaskDataStore } from '../task/task-data-store'
import { AliasDataStore as CollaboratorDataStore } from '@skinsoft/entity'

export class MovementDataStore extends EntityDataStore<Movement, MovementLifecycleStates> {
    public static highlightStates = [MovementLifecycleStates.PROGRAMMED];
    public static featuredStates = [
        MovementLifecycleStates.IN_PREPARATION,
        MovementLifecycleStates.IN_PROGRESS,
        MovementLifecycleStates.PROGRAMMED,
    ]; // the order matters, it is used in the featured lists
    public static historyStates = [MovementLifecycleStates.FINALIZED];
    public static sortFields = ['movementSchema.effectiveDates.startDate.startInterval.date'];

    public static getInstance() {
        if (!MovementDataStore.instance) {
            MovementDataStore.instance = new MovementDataStore();
        }

        return MovementDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$movementDataStore = MovementDataStore.getInstance();
    }

    private static instance: MovementDataStore = null as any;

    constructor() {
        super('movements');
    }

    public async closeMovement(
        movementId: Movement['id'],
        arrivalLocation: string = null as any,
        modifyPermanentLocation: boolean = false,
        usePermanentLocItemAsDestination: boolean = false,
    ) {
        let movement = await this.getMovement(movementId);

        if (movementSelectors.movementType(movement) === MovementTypes.SIMPLE) {
            if (!arrivalLocation && !usePermanentLocItemAsDestination) {
                throw new Error('You must provide an arrival location');
            }

            set(movement, ['movementSchema', 'arrivalLocation'], arrivalLocation);
            set(
                movement,
                ['movementSchema', 'modifyAllPermanentObjectLocations'],
                modifyPermanentLocation,
            );
            set(movement, 'workflowSchema.usePermanentLocationForDestination', usePermanentLocItemAsDestination)

            let movementWithUpdatedItems: Movement = movement
            const linkableItems = movementSelectors.linkableItems(movement);
            const transitionninglinkableItems = linkableItems.filter(linkableItem =>
                        linkableItem.state.name === LinkableItemLifecycleStates.IN_TRANSIT,
                )
            if(usePermanentLocItemAsDestination) {
                const itemsIds = transitionninglinkableItems.map(linkableItem => linkableItem.linkableItem)
                const items = await ItemDataStore.getInstance().searchAll('', undefined, undefined, undefined, undefined, undefined, itemsIds).toPromise()

                for(const item of items.allData) {
                    const permanentLocation = itemSelectors.permanentLocation(item as Item)
                    movementWithUpdatedItems = await this.transitionLinkableItems(movementWithUpdatedItems, [(item as Item).id], LinkableItemLifecycleStates.FINALIZED, permanentLocation, undefined, false)
                }

            } else {
                const transitionninglinkableItemsIds = transitionninglinkableItems.map(linkableItem => linkableItem.linkableItem)
                movementWithUpdatedItems = await this.transitionLinkableItems(movement, transitionninglinkableItemsIds, LinkableItemLifecycleStates.FINALIZED, arrivalLocation, modifyPermanentLocation, false)
            }

            await this.update(movement.id, movement)
                .pipe(first())
                .toPromise();
        }

        return this.transitionMovement(movement.id, MovementLifecycleStates.FINALIZED);
    }

    public cancelMovement(movementId: Movement['id']) {
        return this.transitionMovement(movementId, MovementLifecycleStates.CANCELED);
    }

    public async createReturnMovement(movementId: Movement['id']) {
        const movement = await this.getMovement(movementId);

        const movementType = movementSelectors.movementType(movement)

        if (movementType === 'returning') {
            throw new Error('You cannot create a return movement from a return movement');
        }

        const user = await AuthService.getInstance()
            .user$.pipe(first())
            .toPromise();
        const collaborator = await CollaboratorDataStore.getInstance().getCollaboratorByUserId((user as User).id).toPromise();


        const returnMovement = await this.proxy
            .fetch(`/movements/return`, { movementId: movement.id, typeMovementReturn: movementType }, 'POST')
            .toPromise();


        // Setting current collaborator to storekeeper field    
        returnMovement.movementSchema.affectedStorekeepers = [(collaborator as Collaborator).id];
        await this.update(movement.id, returnMovement).pipe(first()).toPromise();

        return await this.transitionMovement(returnMovement.id, MovementLifecycleStates.PROGRAMMED);
    }

    public async addItem(
        movementId: Movement['id'],
        itemId: Item['id'],
        itemType: string,
        arrivalLocation: string = null as any,
    ) {
        const movement = await this.getMovement(movementId);

        const linkableItems = movementSelectors.linkableItems(movement) || [];
        linkableItems.push({
            arrivalLocation,
            linkableItem: itemId,
            linkableItemReferenceType: itemType,
            state: {
                name: LinkableItemLifecycleStates.SELECTED,
            },
        } as LinkableItem);

        movement.workflowSchema.linkableItems = uniqBy(linkableItems, item => item.linkableItem);

        await this.update(movement.id, movement)
            .pipe(first())
            .toPromise();
    }

    public removeItem(movementId: Movement['id'], itemId: Item['id']) {
        return from(this.getMovement(movementId)).pipe(
            map(movement => ({
                ...movement,
                workflowSchema: {
                    ...movement.workflowSchema,
                    linkableItems: movement.workflowSchema.linkableItems.filter(
                        item => item.linkableItem !== itemId,
                    ),
                },
            })),
            switchMap(movement => this.update(movementId, movement)),
        )
    }

    public async addItems(movementId, newLinkableItems, arrivalLocation: string = null as any) {

        const movement = await this.getMovement(movementId);

        const linkableItems = movementSelectors.linkableItems(movement) || [];

        newLinkableItems.map(
            (item) => {
                linkableItems.push({
                    arrivalLocation,
                    linkableItem: item.linkableItem,
                    linkableItemReferenceType: item.linkableItemReferenceType,
                    state: {
                        name: LinkableItemLifecycleStates.SELECTED,
                    },
                } as LinkableItem)

            }
        )

        movement.workflowSchema.linkableItems = linkableItems;

        await this.update(movement.id, movement)
            .pipe(first())
            .toPromise();

    }

    public async createMovement(
        movementType: MovementTypes.SIMPLE | MovementTypes.COMPLEX,
        taskId?: Task['id'],
        title?: string,
        motive?: string,
    ) {
        const user = await AuthService.getInstance()
            .user$.pipe(first())
            .toPromise();

        const task = taskId
            ? await TaskDataStore.getInstance()
                .fetchOne(taskId, true)
                .pipe(first())
                .toPromise()
            : null;

        const collaborator = await CollaboratorDataStore.getInstance().getCollaboratorByUserId((user as User).id).toPromise();

        const movement = await this.create(
            {
                movementType,
            } as Partial<Movement>,
            {
                movementSchema: {
                    affectedStorekeepers: [(collaborator as Collaborator).id],
                    ...((title || motive) && {
                        ...(title && { title: { translatedString: { fr: title } } }),
                        ...(motive && { movementMotif: motive }),
                    }),
                    ...(task && { associatedTasks: [taskId] }),
                },
            } as Partial<Movement>,
        )
            .pipe(first())
            .toPromise();

        if (!task) {
            return movement;
        }

        const linkableItems = taskSelectors.linkableItems(task);

        await this.addItems(movement.id, linkableItems);

        return this.fetchOne(movement.id, true)
            .pipe(first())
            .toPromise();
    }

    /**
     * 
     * @param movementParam the movement or the id of the movement
     * @param state the state to reach
     * @param linkableItemsToTransitionIds the list of ids of the items we want to transition (will transition all items if undefined)
     * @param arrivalLocation
     * @param modifyPermanentLocation 
     * @param update true -> update the changes on the API ; false -> only return a Movement, no changes are saved
     * @returns a Movement with the linkableItems transitionned
     */
    private async transitionLinkableItemsImplementation(
        movementParam: string | Movement, 
        state: LinkableItemLifecycleStates,
        linkableItemsToTransitionIds?: string[], // null for transitionning all linkable items
        arrivalLocation?: Location['id'],
        modifyPermanentLocation?: boolean,
        update: boolean = true,
    ) {
        let movement: Movement
        if(typeof movementParam === 'string' || movementParam instanceof String) {
            movement = await this.getMovement(movementParam as string)
        } else {
            movement = movementParam
        }

        // transitionning items
        const linkableItems = movementSelectors.linkableItems(movement)
        
        if(linkableItemsToTransitionIds) {
            for(const linkableItemId of linkableItemsToTransitionIds) {
                const index = linkableItems.findIndex(i => i.linkableItem === linkableItemId)
                set(movement, ['workflowSchema', 'linkableItems', index, 'state', 'name'], state)
                if (movementSelectors.movementType(movement) !== MovementTypes.SIMPLE && arrivalLocation) {
                    set(movement, ['workflowSchema', 'linkableItems', index, 'arrivalLocation'], arrivalLocation)
                }
                if(modifyPermanentLocation) {
                    set(movement, ['workflowSchema', 'linkableItems', index, 'modifyPermanentLocation'], modifyPermanentLocation)
                }
            }

        } else {
            // if we update all linkableItems, we don't need to find index
            for(const linkableItem of linkableItems) {
                set(linkableItem, 'state.name', state)
                if (movementSelectors.movementType(movement) !== MovementTypes.SIMPLE && arrivalLocation) {
                    set(linkableItem, 'arrivalLocation', arrivalLocation)
                }
                if(modifyPermanentLocation) {
                    set(linkableItem, 'modifyPermanentLocation', modifyPermanentLocation)
                }
            }
        }
        
        // update movement if needed
        let updatedMovement = movement
        if(update) {
            updatedMovement = await this.update(movement.id, movement)
                .pipe(first())
                .toPromise()
    
            if (movementSelectors.state(movement) === MovementLifecycleStates.PROGRAMMED || movementSelectors.state(movement) === MovementLifecycleStates.IN_PREPARATION) {
                return await this.transitionMovement(movement.id, MovementLifecycleStates.IN_PROGRESS);
            }
        }

        return updatedMovement
    }

    public async transitionLinkableItem(
        movementId: Movement['id'],
        linkableItemId: string,
        state: LinkableItemLifecycleStates,
        arrivalLocation?: Location['id'],
        modifyPermanentLocation?: boolean,
        update: boolean = true,
    ) {
        return this.transitionLinkableItemsImplementation(movementId, state, [linkableItemId], arrivalLocation, modifyPermanentLocation, update)
    }

    public async transitionLinkableItems(
        movement: Movement, 
        linkableItemsIds: string[], 
        state: LinkableItemLifecycleStates, 
        arrivalLocation?: Location['id'],
        modifyPermanentLocation?: boolean,
        update: boolean = true,
    ) {
        return this.transitionLinkableItemsImplementation(movement, state, linkableItemsIds, arrivalLocation, modifyPermanentLocation, update)  
    }

    public async transitionAllLinkableItems(
        movement: Movement, 
        state: LinkableItemLifecycleStates, 
        arrivalLocation?: Location['id'],
        modifyPermanentLocation?: boolean,
        update: boolean = true,
    ) {
        return this.transitionLinkableItemsImplementation(movement, state, undefined, arrivalLocation, modifyPermanentLocation, update)
    }

    private transitionMovement(
        movementId: Movement['id'],
        lifecycleState: MovementLifecycleStates,
    ) {
        return this.proxy
            .fetch(`/movements/tracking`, { id: movementId, lifecycleState }, 'PATCH')
            .pipe(
                concatMap(() => this.fetchOne(movementId, true)),
                first(),
            )
            .toPromise();
    }

    private async getMovement(id: Movement['id']) {
        return cloneDeep(
            await this.fetchOne(id, true)
                .pipe(first())
                .toPromise(),
        );
    }

    public getMovementContainingItems(...itemsIds: String[]): Observable<Movement[]> {
        let query = {
            queryText: "",
            sortObject: null,
            types: ["movement"],
            advancedSearches: [
              {
                leftCriteria: {
                },
                linkOperator: "OR",
                rightCriteria: {
                },
                type: "requestSearch"
              }
            ],
            excludedIds: [],
            page: 0,
            pageSize: 5,
            searchOnArchived: false,
            searchOnVersioned: false
        }

        if(!itemsIds) {
            return of([])
        }

        query.advancedSearches[0].leftCriteria = {
            criteriaList: [
                {
                    operator: "equals",
                    entityField: "5bc44c9ab3cb1f00010f3c1e",
                    field: "workflowSchema.linkableItems.linkableItem",
                    type: "criteria",
                    value: {
                        refId: itemsIds[0],
                        type: "ref"
                    }
                }
            ],
            parenField: "5ae731fa30c49e0001ced553",
            searchOnFirst: false,
            type: "nestedSearch"
        }

        for(let i=1; i<itemsIds.length; i++) {
            const itemCriteria = {
                criteriaList: [
                    {
                        operator: "equals",
                        entityField: "5bc44c9ab3cb1f00010f3c1e",
                        field: "workflowSchema.linkableItems.linkableItem",
                        type: "criteria",
                        value: {
                            refId: itemsIds[i],
                            type: "ref"
                        }
                    }
                ],
                parenField: "5ae731fa30c49e0001ced553",
                searchOnFirst: false,
                type: "nestedSearch"
            }

            if(query.advancedSearches[0].rightCriteria) {
                query.advancedSearches[0].leftCriteria = _.cloneDeep(query.advancedSearches[0])
                query.advancedSearches[0].rightCriteria = itemCriteria
            } else {
                query.advancedSearches[0].rightCriteria = itemCriteria
            }
        }

        const statusCriteria = {
            operator: 'equals',
            value: {
                text: 'En cours',
                type: 'text'
            },
            entityField: '5ae731fa30c49e0001ced552',
            type: 'criteria',
        }

        if(_.isEmpty(query.advancedSearches[0].rightCriteria)) {
            query.advancedSearches[0].rightCriteria = statusCriteria
        } else {
            query.advancedSearches[0].leftCriteria = _.cloneDeep(query.advancedSearches[0])
            query.advancedSearches[0].rightCriteria = statusCriteria
        }
        query.advancedSearches[0].linkOperator = 'AND'
        
        return this.proxy.fetch('/indexSearch/search', undefined, 'POST', query).pipe(
            switchMap(res => {
                return of(res.allData)
            }),
        )
    }
}
